import React, { useState } from 'react';
import { Download, ExternalLink, FileText } from 'lucide-react';
import logger from '../../utils/logger';

// Sous-hook Messagerie : gestion des pièces jointes (téléchargement, ouverture,
// prévisualisation) et des modales liées (sélecteur CRM, enregistrement CRM).
export function useMailAttachments({ backendUrl, authToken, showSuccess, showError }) {
  const [previewModal, setPreviewModal] = useState({ show: false, url: null, filename: null, mimeType: null, emailId: null, attachmentIndex: null });
  const [showCRMAttachmentPicker, setShowCRMAttachmentPicker] = useState(false);
  const [showSaveAttachmentModal, setShowSaveAttachmentModal] = useState(false);
  const [attachmentToSave, setAttachmentToSave] = useState(null);

  const downloadAttachment = async (emailId, attachmentIndex, filename) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/admin/mail/attachment/${emailId}/${attachmentIndex}`,
        {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSuccess('Pièce jointe téléchargée');
      } else {
        throw new Error('Erreur lors du téléchargement');
      }
    } catch (error) {
      logger.error('Error downloading attachment:', error);
      showError('Erreur lors du téléchargement de la pièce jointe');
    }
  };

  const openAttachmentInNewTab = async (emailId, attachmentIndex, filename) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/admin/mail/attachment/${emailId}/${attachmentIndex}`,
        {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        showSuccess('Pièce jointe ouverte dans un nouvel onglet');
      } else {
        throw new Error('Erreur lors de l\'ouverture');
      }
    } catch (error) {
      logger.error('Error opening attachment:', error);
      showError('Erreur lors de l\'ouverture de la pièce jointe');
    }
  };

  const previewAttachment = async (emailId, attachmentIndex, filename) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/admin/mail/attachment/${emailId}/${attachmentIndex}`,
        {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setPreviewModal({ show: true, url, filename, mimeType: blob.type, emailId, attachmentIndex });
      } else {
        throw new Error('Erreur lors de la prévisualisation');
      }
    } catch (error) {
      logger.error('Error previewing attachment:', error);
      showError('Erreur lors de la prévisualisation de la pièce jointe');
    }
  };

  const closePreviewModal = () => {
    if (previewModal.url) {
      window.URL.revokeObjectURL(previewModal.url);
    }
    setPreviewModal({ show: false, url: null, filename: null, mimeType: null, emailId: null, attachmentIndex: null });
  };

  const renderPreviewContent = () => {
    const { url, filename, mimeType } = previewModal;

    if (!url || !mimeType) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-slate-500">Impossible de prévisualiser ce fichier</p>
        </div>
      );
    }

    // Images
    if (mimeType.startsWith('image/')) {
      return (
        <div className="flex items-center justify-center h-full bg-slate-100 overflow-auto">
          <img src={url} alt={filename} className="max-w-full max-h-full object-contain" />
        </div>
      );
    }

    // Vidéos
    if (mimeType.startsWith('video/')) {
      return (
        <div className="flex items-center justify-center h-full bg-black">
          <video controls className="max-w-full max-h-full">
            <source src={url} type={mimeType} />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
        </div>
      );
    }

    // Audio
    if (mimeType.startsWith('audio/')) {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-slate-100">
          <FileText size={64} className="text-slate-400 mb-4" />
          <p className="text-slate-700 mb-4">{filename}</p>
          <audio controls className="w-full max-w-xl">
            <source src={url} type={mimeType} />
            Votre navigateur ne supporte pas la lecture audio.
          </audio>
        </div>
      );
    }

    // PDF
    if (mimeType === 'application/pdf') {
      return <iframe src={url} className="w-full h-full border-0" title={filename} />;
    }

    // Documents Office (Word, Excel, PowerPoint)
    if (
      mimeType.includes('word') ||
      mimeType.includes('excel') ||
      mimeType.includes('spreadsheet') ||
      mimeType.includes('presentation') ||
      mimeType.includes('officedocument') ||
      filename.match(/\.(doc|docx|xls|xlsx|ppt|pptx)$/i)
    ) {
      const fileExtension = filename.split('.').pop().toUpperCase();
      const getFileIcon = () => {
        if (filename.match(/\.(xls|xlsx)$/i)) return '📊';
        if (filename.match(/\.(doc|docx)$/i)) return '📄';
        if (filename.match(/\.(ppt|pptx)$/i)) return '📽️';
        return '📁';
      };

      const handleDownload = () => {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showSuccess('Téléchargement en cours...');
      };

      return (
        <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-slate-50 to-slate-100 p-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
            <div className="text-center">
              <div className="text-6xl mb-4">{getFileIcon()}</div>
              <div className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold mb-3">
                {fileExtension}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 break-all">{filename}</h3>
              <p className="text-slate-600 mb-6">
                Les fichiers Microsoft Office ne peuvent pas être prévisualisés directement dans le navigateur.
              </p>

              <div className="space-y-3">
                <button
                  onClick={handleDownload}
                  className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                >
                  <Download size={20} />
                  <span>Télécharger et ouvrir</span>
                </button>

                <button
                  onClick={() => {
                    window.open(url, '_blank');
                    showSuccess('Fichier ouvert dans un nouvel onglet');
                  }}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                >
                  <ExternalLink size={20} />
                  <span>Ouvrir dans un nouvel onglet</span>
                </button>

                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    💡 Le fichier s'ouvrira avec l'application par défaut de votre système (Excel, Word, PowerPoint)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Fichiers texte
    if (mimeType.startsWith('text/') || mimeType === 'application/json') {
      return <iframe src={url} className="w-full h-full border-0 bg-white" title={filename} />;
    }

    // Autres types : message d'information
    return (
      <div className="flex flex-col items-center justify-center h-full bg-slate-100">
        <FileText size={64} className="text-slate-400 mb-4" />
        <p className="text-slate-700 text-lg font-medium mb-2">{filename}</p>
        <p className="text-slate-500 mb-6">Prévisualisation non disponible pour ce type de fichier</p>
        <div className="flex space-x-3">
          <button
            onClick={() => window.open(previewModal.url, '_blank')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <ExternalLink size={18} />
            <span>Ouvrir dans un nouvel onglet</span>
          </button>
        </div>
      </div>
    );
  };

  return {
    previewModal, setPreviewModal,
    showCRMAttachmentPicker, setShowCRMAttachmentPicker,
    showSaveAttachmentModal, setShowSaveAttachmentModal,
    attachmentToSave, setAttachmentToSave,
    downloadAttachment, openAttachmentInNewTab, previewAttachment, closePreviewModal, renderPreviewContent
  };
}
